import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  AssignSubscriptionRequest,
  BillingCatalog,
  PageResponse,
  PaidTier,
  Subscription,
  SubscriptionListItem,
  SubscriptionListQuery,
} from '../../types/subscription.ts';

export interface SubscriptionState {
  subscription?: Subscription;
  isLoadingSubscription: boolean;
  subscriptionError: string;
  allSubscriptions?: PageResponse<SubscriptionListItem>;
  /** Last query of the admin list, so it can be reloaded as-is after assigning or cancelling. */
  allSubscriptionsQuery: SubscriptionListQuery;
  isLoadingAllSubscriptions: boolean;
  isAssigningSubscription: boolean;
  assignSubscriptionSuccess: boolean;
  isCancellingSubscription: boolean;
  cancelSubscriptionSuccess: boolean;
  billingCatalog?: BillingCatalog;
  isLoadingBillingCatalog: boolean;
  /** A checkout or the billing portal is being opened; the browser is about to leave the app. */
  isOpeningPaymentPage: boolean;
  isSwitchingToFree: boolean;
}

const initialState: SubscriptionState = {
  subscription: undefined,
  isLoadingSubscription: false,
  subscriptionError: '',
  allSubscriptions: undefined,
  allSubscriptionsQuery: {},
  isLoadingAllSubscriptions: false,
  isAssigningSubscription: false,
  assignSubscriptionSuccess: false,
  isCancellingSubscription: false,
  cancelSubscriptionSuccess: false,
  billingCatalog: undefined,
  isLoadingBillingCatalog: false,
  isOpeningPaymentPage: false,
  isSwitchingToFree: false,
};

export const subscriptionReducer = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    loadSubscription: (state: SubscriptionState) => {
      state.isLoadingSubscription = true;
      state.subscriptionError = '';
    },
    setSubscription: (state: SubscriptionState, action: PayloadAction<Subscription>) => {
      state.isSwitchingToFree = false;
      state.subscription = action.payload;
      state.isLoadingSubscription = false;
      state.subscriptionError = '';
    },
    setSubscriptionError: (state: SubscriptionState, action: PayloadAction<string>) => {
      state.isLoadingSubscription = false;
      state.isLoadingAllSubscriptions = false;
      state.isAssigningSubscription = false;
      state.isCancellingSubscription = false;
      state.subscriptionError = action.payload;
    },
    loadAllActiveSubscriptions: (state: SubscriptionState, action: PayloadAction<SubscriptionListQuery>) => {
      state.allSubscriptionsQuery = action.payload;
      state.isLoadingAllSubscriptions = true;
      state.subscriptionError = '';
    },
    setAllActiveSubscriptions: (
      state: SubscriptionState,
      action: PayloadAction<PageResponse<SubscriptionListItem>>,
    ) => {
      state.allSubscriptions = action.payload;
      state.isLoadingAllSubscriptions = false;
      state.subscriptionError = '';
    },
    assignSubscription: (
      state: SubscriptionState,
      _action: PayloadAction<{ userId: number; request: AssignSubscriptionRequest }>,
    ) => {
      state.isAssigningSubscription = true;
      state.assignSubscriptionSuccess = false;
      state.subscriptionError = '';
    },
    setAssignSubscriptionSuccess: (state: SubscriptionState) => {
      state.isAssigningSubscription = false;
      state.assignSubscriptionSuccess = true;
      state.subscriptionError = '';
    },
    cancelSubscription: (state: SubscriptionState, _action: PayloadAction<number>) => {
      state.isCancellingSubscription = true;
      state.cancelSubscriptionSuccess = false;
      state.subscriptionError = '';
    },
    setCancelSubscriptionSuccess: (state: SubscriptionState) => {
      state.isCancellingSubscription = false;
      state.cancelSubscriptionSuccess = true;
      state.subscriptionError = '';
    },
    loadBillingCatalog: (state: SubscriptionState) => {
      state.isLoadingBillingCatalog = true;
    },
    setBillingCatalog: (state: SubscriptionState, action: PayloadAction<BillingCatalog | undefined>) => {
      state.billingCatalog = action.payload;
      state.isLoadingBillingCatalog = false;
    },
    startSubscriptionCheckout: (state: SubscriptionState, _action: PayloadAction<PaidTier>) => {
      state.isOpeningPaymentPage = true;
    },
    startCreditPackCheckout: (state: SubscriptionState, _action: PayloadAction<string>) => {
      state.isOpeningPaymentPage = true;
    },
    openBillingPortal: (state: SubscriptionState) => {
      state.isOpeningPaymentPage = true;
    },
    setOpeningPaymentPageFailed: (state: SubscriptionState) => {
      state.isOpeningPaymentPage = false;
    },
    /** Give up a plan that is not paid for and move to Free. */
    switchToFree: (state: SubscriptionState) => {
      state.isSwitchingToFree = true;
    },
    setSwitchToFreeFailed: (state: SubscriptionState) => {
      state.isSwitchingToFree = false;
    },
    resetSubscriptionStatus: (state: SubscriptionState) => {
      state.assignSubscriptionSuccess = false;
      state.cancelSubscriptionSuccess = false;
      state.subscriptionError = '';
    },
  },
});
